import {
  forwardRef,
  useId,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cx } from './cx.js';
import { Icon, type IconName } from './Icon.tsx';

/**
 * A labelled input.
 *
 * The label is an element, not a placeholder. A placeholder disappears the
 * moment somebody types, which means the one time they need to check what the
 * box was for is the one time it is gone.
 *
 * An error is announced as well as shown: `aria-invalid` plus a described-by
 * message, so it reaches somebody who cannot see the colour. The message names
 * what is wrong and what to do, never "invalid".
 */

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  label: string;
  /** Sits under the input, always present when given - not a tooltip. */
  hint?: string;
  /** Names what is wrong and what to do about it. */
  error?: string;
  /** A lucide icon drawn inside the field, before the text. */
  icon?: IconName;
  /** Shown beside the label. Absence of this does not imply optional. */
  optionalLabel?: string;
  className?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, icon, optionalLabel, className, required, ...rest },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div data-component="Field" className={cx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="flex items-baseline gap-2 text-dense font-medium text-ink">
        {label}
        {optionalLabel ? <span className="text-meta font-regular text-ink-meta">{optionalLabel}</span> : null}
      </label>

      <div className="relative flex items-center">
        {icon ? (
          <span className="pointer-events-none absolute start-3 flex items-center text-ink-400">
            <Icon name={icon} size="inline" decorative />
          </span>
        ) : null}
        <input
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cx(
            'h-input w-full rounded-control border bg-surface text-body text-ink',
            'placeholder:text-ink-300',
            'transition-colors duration-state ease-out',
            'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-disabled',
            icon ? 'ps-input pe-input-x' : 'px-input-x',
            error ? 'border-danger' : 'border-control',
          )}
          {...rest}
        />
      </div>

      {hint ? (
        <p id={hintId} className="text-meta text-ink-meta">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="flex items-start gap-1 text-meta text-danger">
          <Icon name="Warning" size="inline" decorative className="mt-px shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
});

/** A group of fields under one heading, with the design system's spacing. */
export function FieldSet({
  legend,
  children,
  className,
  ...rest
}: {
  legend?: string;
  children: ReactNode;
  className?: string;
} & HTMLAttributes<HTMLFieldSetElement>): JSX.Element {
  return (
    <fieldset {...rest} data-component="FieldSet" className={cx('flex flex-col gap-4 border-0 p-0', className)}>
      {legend ? (
        <legend className="mb-1 text-micro uppercase tracking-label text-ink-label">{legend}</legend>
      ) : null}
      {children}
    </fieldset>
  );
}

/**
 * A form-level message.
 *
 * Sits above the fields for things that are not any single field's fault - a
 * rejected sign-in, a rule that fired across several values. Field-level
 * problems belong on the field.
 */
export function FormMessage({
  tone,
  children,
  ...rest
}: {
  tone: 'error' | 'info' | 'success';
  children: ReactNode;
} & HTMLAttributes<HTMLParagraphElement>): JSX.Element {
  const toneClass =
    tone === 'error' ? 'text-danger' : tone === 'success' ? 'text-success' : 'text-info';
  const iconName = tone === 'error' ? 'Warning' : tone === 'success' ? 'CheckCircle' : 'Info';
  return (
    <p
      {...rest}
      data-component="FormMessage"
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('flex items-start gap-2 text-dense', toneClass)}
    >
      <Icon name={iconName} size="inline" decorative className="mt-px shrink-0" />
      <span>{children}</span>
    </p>
  );
}
